import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import UAParser from 'ua-parser-js';
import { v1 as uuidv1 } from 'uuid';
import { LicenseInfo } from '@mui/x-license-pro';
import * as Sentry from '@sentry/react';

/**
 * Paramétrage de l'application
 */
const {
  REACT_APP_IS_CLOCKING_APP: envCometeOnTime,
  REACT_APP_CI: continuousIntegration,
  NODE_ENV: nodeEnv
} = process.env;

// Sentry
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: nodeEnv,
    enabled: process.env.NODE_ENV !== 'development',
    release: process.env.REACT_APP_VERSION,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    attachStacktrace: true,
    normalizeDepth: 6,
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0
  });

  // Intercepter console.error pour envoyer les erreurs à Sentry
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const errorMessage = args.map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg))).join(' ');
    Sentry.captureMessage(errorMessage, 'error');

    originalConsoleError.apply(console, args);
  };
}

// Mui-x-pro licence
if (process.env.REACT_APP_MUI_KEY) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY);
}

// User agent
// ? https://docs.uaparser.dev/api/ua-parser-js/get-result.html
const parser = new UAParser();
const uaDeviceInfo = parser.getResult();

// Capacitor
// ? https://capacitorjs.com/docs/apis/device

// Environnement informations
const isDev = !!continuousIntegration || nodeEnv === 'development';

// Sécurité : un navigateur ayant servi à paramétrer un appareil Comète On Time
// (présence de `comete_device_type` en localStorage) doit être verrouillé sur /pointages.
// Sans cela, une session manager active permettrait d'accéder à l'espace Link en
// retirant /pointages de l'URL.
// On redirige directement vers /pointages (et non /pointages/login) pour éviter un flash
// du login : si l'utilisateur est déjà authentifié, LoginPage le re-naviguerait vers /
// puis GuestGuard le ramènerait sur /pointages.
const hasCometeDeviceType = !!localStorage.getItem('comete_device_type');
if (hasCometeDeviceType && !location.pathname.includes('/pointages')) {
  location.replace(`/pointages${location.search || ''}`);
}

const isCometeOnTime = envCometeOnTime === 'true' || location.pathname.includes('/pointages') || hasCometeDeviceType;
const osName = Capacitor.getPlatform();
const isMobileDevice = !!uaDeviceInfo.device.type; // 'mobile', 'tablet', or undefined (for desktop)

// Universally unique identifier
let uuid = localStorage.getItem('uuid');
if (!uuid) {
  uuid = uuidv1();
  localStorage.setItem('uuid', uuid);
}

declare global {
  /**
   * Déclaration d'extension de l'interface Window
   * @interface
   */
  interface Window {
    /**
     * Variables d'environnement
     * @property {string} PUBLIC_URL - URL de l'application
     * @property {number | undefined} SOCKET_PORT - Port du webservice
     * @property {string} platform - Plateforme
     * @property {string} browser - Navigateur
     * @property {string} deviceName - Nom du device
     * @property {string} deviceModel - Modèle du device
     * @property {string} deviceType - Type du device
     * @property {string} deviceVendor - Fabricant du device
     * @property {string} osName - Nom du système d'exploitation
     * @property {string} osVersion - Version du système d'exploitation
     * @property {boolean} isApp - Application mobile
     * @property {boolean} isAndroid - Android
     * @property {boolean} isIos - iOS
     * @property {boolean} isClockingApp - Application de pointage
     * @property {boolean} isMobileDevice - Mobile
     * @property {boolean} isDev - Développement
     * @property {string} uuid - UUID
     */
    env: {
      PUBLIC_URL: string;
      SOCKET_PORT?: number;
      platform: string;
      browser: string | undefined;
      deviceName: string;
      deviceModel: string | undefined;
      deviceType: string;
      deviceVendor: string | undefined;
      osName: string;
      osVersion: string | undefined;
      isApp: boolean;
      isAndroid: boolean;
      isIos: boolean;
      isClockingApp: boolean;
      isMobileDevice: boolean;
      isDev: boolean;
      uuid: string;
      /** URL de l'API REST (renseignée après connexion socket) */
      apiUrl?: string | null;
    };
  }
}

// Initialisation des variables d'environnement
window.env = {
  ...(process.env.SOCKET_PORT ? { SOCKET_PORT: Number(process.env.SOCKET_PORT) } : {}),
  PUBLIC_URL: process.env.PUBLIC_URL || '',
  platform: osName,
  browser: uaDeviceInfo.browser.name,
  deviceName: uaDeviceInfo.ua, // web browsers can't access to the device name
  deviceModel: uaDeviceInfo.device.model, // web browsers can't always access to the device model
  deviceType: uaDeviceInfo.device.type || 'desktop', // type is undefined on desktop devices
  deviceVendor: uaDeviceInfo.device.vendor, // web browsers can't always access to the device vendor
  osName,
  osVersion: uaDeviceInfo.os.version,
  isApp: Capacitor.isNativePlatform(),
  isAndroid: osName === 'android',
  isIos: osName === 'ios',
  isClockingApp: isCometeOnTime ?? false,
  isMobileDevice,
  isDev,
  uuid
};

/**
 * Récupération des informations du device
 */
Device.getInfo()
  .then((info) => {
    window.env.osName = info.operatingSystem;
    window.env.osVersion = info.osVersion;
    window.env.deviceVendor = info.manufacturer;
    window.env.deviceModel = info.model;
  })
  .catch(console.error);
